import {
  Alert,
  Button as AmbrosiaButton,
  AnchorButton,
  Card,
  InputGroup,
  Modal,
  useToastContext,
} from '@payaca/ambrosia';
import { clstx } from '@payaca/utilities/cvaMerge';
import {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type JSX,
} from 'react';
import { useDispatch } from 'react-redux';

import BasicField from '@payaca/components/basicField/BasicField';
import { InputStyleVariant } from '@payaca/components/inputWrapper/InputWrapper';
import PaymentMethodIcons from '@payaca/components/paymentMethodIcons/PaymentMethodIcons';
import SwitchField from '@payaca/components/switchField/SwitchField';
import TextareaFieldFormatter, {
  ToolbarColourVariant,
} from '@payaca/components/textareaField/TextareaFieldFormatter';
import ValidatedFieldWrapper from '@payaca/components/validatedFieldWrapper/ValidatedFieldWrapper';
import AccountNameField from '../bacsFields/AccountNameField';
import AccountNumberField from '../bacsFields/AccountNumberField';
import SortCodeField from '../bacsFields/SortCodeField';

import { getRegionalTextString } from '@payaca/helpers/internationalHelper';
import { AccountRegions } from '@payaca/types/accountTypes';
import { RegionalStrings } from '@payaca/types/internationalTypes';
import { get } from 'lodash-es';

import { actions as usersActions } from '@/api/users';
import { useGettingPaidFormValidators } from '@/hooks/useCompanyInfoValidators';
import { useFormState } from '@/hooks/useFormState';
import { useUserHasPermission } from '@/hooks/usePermissions';
import { useAccount } from '@/utils/storeHooks';
import { AccountsPermissions } from '@payaca/permissions/accounts/accounts.permissions';

import { FieldValidationResult } from '@payaca/types/fieldValidationTypes';
import CompanySettingsTabBase from './CompanySettingsTabBase';

type Props = {
  readOnly: boolean;
};

type FormState = {
  allowStripePaymentDefault: boolean;
  allowBankTransferPaymentDefault: boolean;
  accountName: string;
  sortCode: string;
  accountNumber: string;
  vatNumber: number;
  proposalPaymentTerms: string;
  invoicePaymentTerms: string;
  externalPaymentRedirectUrl: string;
};

type FormValidationResult = Partial<
  Record<keyof FormState, FieldValidationResult>
>;

const STRIPE_CLIENT_ID = import.meta.env.VITE_STRIPE_CLIENT_ID;

const CompanySettingsGettingPaid: FC<Props> = ({
  readOnly,
}: Props): JSX.Element => {
  const dispatch = useDispatch();
  const [showStripeModal, setShowStripeModal] = useState(false);

  const account = useAccount();

  const canManageExternalPaymentRedirect = useUserHasPermission({
    permissions: [AccountsPermissions.MANAGE_EXTERNAL_PAYMENT_REDIRECT],
  });

  const initialState: FormState = {
    allowStripePaymentDefault: account.allowStripePaymentDefault,
    allowBankTransferPaymentDefault: account.allowBankTransferPaymentDefault,
    accountName: account.accountNameRaw ?? '',
    sortCode: account.sortCodeRaw ?? '',
    accountNumber: account.accountNumberRaw ?? null,
    vatNumber: account.vatNumber ?? null,
    proposalPaymentTerms: account.proposalPaymentTerms ?? '',
    invoicePaymentTerms: account.invoicePaymentTerms ?? '',
    externalPaymentRedirectUrl: account.externalPaymentRedirectUrl ?? '',
  };

  const fieldValidators = useGettingPaidFormValidators(account.region);
  const { pushSuccess, pushError } = useToastContext();

  const saveFormData = useMemo(
    () => async (state: FormState) => {
      return new Promise<void>((resolve, reject) => {
        dispatch(
          usersActions.updateBusinessAccount(
            account.id,
            state,
            (err: unknown, response: any) => {
              if (err) {
                pushError(`We couldn't save your changes`);
                reject(err);
              } else {
                pushSuccess('Your changes have been saved');
                resolve();
              }
            }
          )
        );
      });
    },
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useMemo has missing dependencies: 'dispatch', 'pushSuccess', 'pushError', and 'account.id'
    []
  );

  const { formState, updateFormFields, formValidationResult } = useFormState<
    FormState,
    FormValidationResult
  >(initialState, fieldValidators, {
    autoSaveFn: saveFormData,
  });

  const isConnectedToStripe = useMemo(
    () => account && account.connectedToStripe,
    [account]
  );

  const interval = useRef<any>(null);

  useEffect(() => {
    return () => clearInterval(interval.current);
  }, []);

  const setCheckStripeStatusInterval = () => {
    clearInterval(interval.current);
    interval.current = setInterval(() => checkStripeStatus(), 3000);
  };

  const checkStripeStatus = () => {
    dispatch(
      usersActions.getProfile((err: any, response: any) => {
        const connectedToStripe = get(response, [
          'accounts',
          0,
          'connectedToStripe',
        ]);

        if (connectedToStripe) {
          updateFormFields({
            // allowStripePaymentDefaultChange: true,
            allowStripePaymentDefault: true, // not sure if this is right - not sure how to test it
          });
          clearInterval(interval.current);
        } else {
          updateFormFields({
            allowStripePaymentDefault: false,
          });
        }
      })
    );
  };

  const onAllowStripePaymentDefaultChange = useCallback(
    (allowStripePaymentDefault: boolean) => {
      if (isConnectedToStripe) {
        updateFormFields({
          allowStripePaymentDefault: allowStripePaymentDefault,
        });
      } else {
        setCheckStripeStatusInterval();
        if (allowStripePaymentDefault) {
          setShowStripeModal(true);
        } else {
          updateFormFields({
            allowStripePaymentDefault: false,
          });
        }
      }
    },
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useCallback has a missing dependency: 'setCheckStripeStatusInterval'
    [isConnectedToStripe, updateFormFields]
  );

  const vatRegionalLabel = useMemo(
    () =>
      getRegionalTextString(
        account.region,
        RegionalStrings.VALUE_ADDED_TAX_NUMBER
      ),
    [account]
  );

  const hasExternalPaymentRedirect = !!formState.externalPaymentRedirectUrl;

  const allowBankTransferPaymentDefaultAvailable = useMemo(() => {
    return (
      !!formState.sortCode &&
      !!formState.accountName &&
      !!formState.accountNumber
    );
  }, [formState]);

  useEffect(() => {
    if (
      !allowBankTransferPaymentDefaultAvailable &&
      formState.allowBankTransferPaymentDefault
    ) {
      updateFormFields({
        allowBankTransferPaymentDefault: false,
      });
    }
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'updateFormFields'
  }, [allowBankTransferPaymentDefaultAvailable]);

  useEffect(() => {
    if (!account.connectedToStripe && formState.allowStripePaymentDefault) {
      updateFormFields({
        allowStripePaymentDefault: false,
      });
    }
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'updateFormFields'
  }, [account?.connectedToStripe]);

  return (
    <CompanySettingsTabBase>
      <Card sizeVariant="lg">
        <Card.Body className="flex flex-col gap-2">
          <h2>Payment method defaults</h2>
          {hasExternalPaymentRedirect && (
            <Alert colour="yellow" className="mt-2 mb-3">
              Payment toggles are unavailable when an external payment redirect
              is configured.
            </Alert>
          )}
          <SwitchField
            name="allowStripePaymentDefault"
            value={formState.allowStripePaymentDefault}
            onChange={(value) => {
              onAllowStripePaymentDefaultChange(
                value.allowStripePaymentDefault
              );
            }}
            label={
              <span
                className={clstx(
                  'flex flex-row flex-wrap gap-2',
                  hasExternalPaymentRedirect && 'opacity-50'
                )}
              >
                <span>Payments with Stripe </span>
                <PaymentMethodIcons />{' '}
                <a
                  href="https://stripe.com/gb/guides/payment-methods-guide"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  + many more
                </a>
              </span>
            }
            isDisabled={readOnly || hasExternalPaymentRedirect}
          />
          <div className="bank-transfer-switch-field-container flex-container flex-center">
            <SwitchField
              name="allowBankTransferPaymentDefault"
              value={formState.allowBankTransferPaymentDefault}
              onChange={updateFormFields}
              label={
                <span
                  className={clstx(hasExternalPaymentRedirect && 'opacity-50')}
                >
                  Direct bank transfer
                </span>
              }
              isDisabled={
                readOnly ||
                !allowBankTransferPaymentDefaultAvailable ||
                hasExternalPaymentRedirect
              }
            />
            {!allowBankTransferPaymentDefaultAvailable &&
              !hasExternalPaymentRedirect && (
                <small className="enter-bank-details-prompt">
                  Enter bank details below to enable this option
                </small>
              )}
          </div>
        </Card.Body>
      </Card>

      <Card sizeVariant="lg">
        <Card.Body className="flex flex-col gap-2">
          <h2>External payment redirect</h2>
          <p className="supporting-body">
            Customers will be redirected to this URL to pay for invoices,
            instead of the normal Stripe and direct bank payment options.
          </p>

          <ValidatedFieldWrapper
            validationResult={formValidationResult.externalPaymentRedirectUrl}
          >
            <InputGroup className="shadow-none">
              <InputGroup.Input
                name="externalPaymentRedirectUrl"
                value={formState.externalPaymentRedirectUrl}
                onChange={(value) =>
                  updateFormFields({ externalPaymentRedirectUrl: value })
                }
                disabled={readOnly || !canManageExternalPaymentRedirect}
                className="border-pc-blue-light-mid"
              />
              <InputGroup.Button
                variant="outline"
                onClick={() =>
                  updateFormFields({ externalPaymentRedirectUrl: '' })
                }
                disabled={
                  readOnly ||
                  !canManageExternalPaymentRedirect ||
                  !hasExternalPaymentRedirect
                }
              >
                Clear
              </InputGroup.Button>
            </InputGroup>
          </ValidatedFieldWrapper>
        </Card.Body>
      </Card>

      <Card sizeVariant="lg">
        <Card.Body className="flex flex-col gap-2">
          <h2>Bank details for direct bank transfer</h2>
          <Alert colour="yellow" className="mt-2 mb-3">
            <p>
              We recommend using <strong>Stripe</strong> even for bank payments,
              as it allows us to mark Invoices as paid automatically.
            </p>
          </Alert>
          <AccountNameField
            name="accountName"
            value={formState.accountName}
            onChange={updateFormFields}
            isReadOnly={readOnly}
          />
          <ValidatedFieldWrapper
            validationResult={formValidationResult.sortCode}
          >
            <SortCodeField
              name="sortCode"
              value={formState.sortCode}
              onChange={updateFormFields}
              isReadOnly={readOnly}
            />
          </ValidatedFieldWrapper>
          <ValidatedFieldWrapper
            validationResult={formValidationResult.accountNumber}
          >
            <AccountNumberField
              name="accountNumber"
              value={formState.accountNumber}
              onChange={updateFormFields}
              isReadOnly={readOnly}
            />
          </ValidatedFieldWrapper>
        </Card.Body>
      </Card>

      <Card sizeVariant="lg">
        <Card.Body className="flex flex-col gap-2">
          <h2>VAT and payment terms</h2>
          <ValidatedFieldWrapper
            validationResult={formValidationResult.vatNumber}
          >
            <BasicField
              name="vatNumber"
              label={vatRegionalLabel}
              value={formState.vatNumber}
              styleVariant={InputStyleVariant.OUTSIZE}
              onChange={updateFormFields}
              textBefore={
                [AccountRegions.UK].includes(account.region) ? 'GB-' : undefined
              }
              isDisabled={readOnly}
            />
          </ValidatedFieldWrapper>
          <ValidatedFieldWrapper
            validationResult={formValidationResult.proposalPaymentTerms}
          >
            <TextareaFieldFormatter
              label="Payment terms shown on Proposals"
              value={formState.proposalPaymentTerms}
              name="proposalPaymentTerms"
              onChange={updateFormFields}
              isDisabled={readOnly}
              placeholder="You can describe additional payment terms here. This will be added to the bottom of your Proposals."
              toolbarColourVariant={ToolbarColourVariant.SECONDARY}
            />
          </ValidatedFieldWrapper>

          <ValidatedFieldWrapper
            validationResult={formValidationResult.invoicePaymentTerms}
          >
            <TextareaFieldFormatter
              label="Payment terms shown on Invoices"
              value={formState.invoicePaymentTerms}
              name="invoicePaymentTerms"
              onChange={updateFormFields}
              isDisabled={readOnly}
              placeholder="You can describe additional payment terms here. This will be added to the bottom of your Invoices."
              toolbarColourVariant={ToolbarColourVariant.SECONDARY}
            />
          </ValidatedFieldWrapper>
        </Card.Body>
      </Card>
      <Modal
        isOpen={showStripeModal}
        title="Accept card payments with Stripe"
        onClose={() => setShowStripeModal(false)}
        size="sm"
      >
        <Modal.Body>
          <div className="flex flex-col gap-2.5">
            <p>
              Stripe is a secure payment platform, trusted by over a million
              businesses all over the world.
            </p>
            <p>
              If you already have a Stripe account you can connect to it or if
              you don't you can create one.
            </p>
            <ul className="list-none space-y-1 p-0">
              <li>
                Make it easy for customers to pay fast with a button on your
                quotes and invoices.
              </li>
              <li>Fast, easy setup</li>
              <li>
                Fees for European cards are 1.4% +20p, you can find out more{' '}
                <a
                  href="https://stripe.com/gb/pricing"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  here
                </a>
                .
              </li>
            </ul>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Modal.Footer.Actions>
            <AmbrosiaButton
              variant="outline"
              onClick={() => setShowStripeModal(false)}
            >
              No thanks
            </AmbrosiaButton>
            <AnchorButton
              href={`https://connect.stripe.com/oauth/authorize?response_type=code&client_id=${STRIPE_CLIENT_ID}&scope=read_write&redirect_uri=${window.location.origin}/enableCustomerPayments`}
            >
              Connect with Stripe
            </AnchorButton>
          </Modal.Footer.Actions>
        </Modal.Footer>
      </Modal>
    </CompanySettingsTabBase>
  );
};

export default CompanySettingsGettingPaid;
